import {useState, useEffect, useCallback} from "react";
import {doc, onSnapshot, setDoc} from "firebase/firestore";
import firebase from "config/firebase";
import useAuthContext from "contexts/Auth/useAuthContext";

interface NotificationDots {
  hasUnreadAnnouncements: boolean;
  hasUnseenCalendar: boolean;
  markCalendarSeen: () => void;
  markAnnouncementsRead: () => void;
}

export default function useNotificationDots(hasTodayEvents: boolean): NotificationDots {
  const auth = useAuthContext();
  const uid = auth?.uid ?? null;
  const todayDate = new Date().toISOString().slice(0, 10);

  const [lastMessageAt, setLastMessageAt] = useState<string | null>(null);
  const [lastReadAt, setLastReadAt] = useState<string | null>(null);
  const [calendarSeenDate, setCalendarSeenDate] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!uid) return;

    let metaLoaded = false;
    let userLoaded = false;
    const trySetLoaded = () => {
      if (metaLoaded && userLoaded) setLoaded(true);
    };

    const unsubMeta = onSnapshot(doc(firebase.db, "configs", "systemMessages"), (snap) => {
      setLastMessageAt(snap.data()?.lastMessageAt ?? null);
      metaLoaded = true;
      trySetLoaded();
    });

    const unsubUser = onSnapshot(doc(firebase.db, "users", uid), (snap) => {
      const data = snap.data();
      setLastReadAt(data?.systemMessagesLastReadAt ?? null);
      setCalendarSeenDate(data?.calendarLastSeenDate ?? null);
      userLoaded = true;
      trySetLoaded();
    });

    return () => {
      unsubMeta();
      unsubUser();
    };
  }, [uid]);

  const markCalendarSeen = useCallback(() => {
    if (!uid) return;
    setCalendarSeenDate(todayDate);
    setDoc(doc(firebase.db, "users", uid), {calendarLastSeenDate: todayDate}, {merge: true}).catch(() => {});
  }, [uid, todayDate]);

  const markAnnouncementsRead = useCallback(() => {
    if (!uid) return;
    const now = new Date().toISOString();
    setLastReadAt(now);
    setDoc(doc(firebase.db, "users", uid), {systemMessagesLastReadAt: now}, {merge: true}).catch(() => {});
  }, [uid]);

  if (!loaded) {
    return {
      hasUnreadAnnouncements: false,
      hasUnseenCalendar: false,
      markCalendarSeen,
      markAnnouncementsRead,
    };
  }

  return {
    hasUnreadAnnouncements: lastMessageAt != null && (lastReadAt == null || lastReadAt < lastMessageAt),
    hasUnseenCalendar: hasTodayEvents && calendarSeenDate !== todayDate,
    markCalendarSeen,
    markAnnouncementsRead,
  };
}
